import { Pub, State, Sub } from '../../../../../shared/state/state.abstract';
import { ITabState, tabStateModel } from '../../../states/tabState.model';
import {
  customerService,
  Bakery2bAccountsRes,
  DisconnectBakery2bAccountFromCustomerRes,
  ConnectBakery2bUserWithWawiCustomerRes,
} from '../../../services/customer.service';
import { Observable, filter, map, merge, switchMap, tap } from 'rxjs';
import { responseHandler } from '../../../../../shared/responseHandler/responseHandler';
import { customersListState } from '../../../states/customersList.state';
import { customerTabLoadingService } from '../customerTabLoading.service';
import { defaultCustomerBakery2bData } from '../../../loaders/bakery2bTab.resolver';
import {
  C_Save_Operation_Status,
  DisconnectWa_Bakery2bAccountFromCustomerMutationVariables,
  Wa_ConnectBakery2bUserInput,
} from '../../../../../graphql/generatedModel';
import { resolveBakery2bConnectError } from '../../../services/bakery2bConnectError';
import { snackbarService } from '../../../../../shared/components/snackbar/service/snackbar.service';
import { t } from 'i18next';
import { SaveBakery2bClientMutationRes } from '../../../../settings/bakery2b/services/bakery2b.service';

class PubImpl extends Pub<IBakery2bTabState> {
  initBakery2bAccountsData(bakery2bAccounts: ITabState['bakery2bAccounts'], customerId: string) {
    this.emit('initBakery2bAccountsData', {
      bakery2bAccounts,
      customerId,
    });
  }

  addRecord(newRecord: Wa_ConnectBakery2bUserInput) {
    this.emit('addRecord', { newRecord });
  }

  deleteRecord(recordToRemove: DisconnectWa_Bakery2bAccountFromCustomerMutationVariables) {
    this.emit('deleteRecord', { recordToRemove });
  }

  saveRecord(recordToSave: IBakery2bTabState['recordToSave']): void {
    this.emit('saveRecord', { recordToSave });
  }

  clearStream() {
    this.emit(undefined, {});
  }
}

class SubImpl extends Sub<IBakery2bTabState> {
  protected actionHandlers(): Observable<IBakery2bTabState> {
    return merge(
      this.selectedCustomer(),
      this.addRecord(),
      this.deleteRecord(),
      this.saveRecord(),
    ).pipe(
      tap((state) => {
        this.stream$.next({ ...state, action: 'internalUpdate' });
      }),
    );
  }

  private saveRecord(): Observable<IBakery2bTabState> {
    return this.actionListener('saveRecord').pipe(
      filter(({ recordToSave }) => !!recordToSave),
      tap(({ recordToSave }) => {
        customerService.pub.saveBakery2bClient({
          dataToSave: {
            id: recordToSave!.id,
            note: recordToSave!.note,
            name: recordToSave!.name,
            orderAddNewItem: recordToSave!.orderAddNewItem,
            orderShowPrice: recordToSave!.orderShowPrice,
            orderNoDelivery: recordToSave!.orderNoDelivery,
            standardOrderEdit: recordToSave!.standardOrderEdit,
          },
        });
        snackbarService.pub.show({
          id: 'savingBakery2bClient',
          content: t('common.saving_in_progress'),
          type: 'loading',
          noAutoHide: true,
        });
      }),
      switchMap(() => {
        return customerService.sub.saveBakery2bClient().pipe(
          responseHandler<SaveBakery2bClientMutationRes>({
            errorReturnType: {
              status: C_Save_Operation_Status.SOS0_DATA_UNCHANGED,
            },
            quite: true,
          }),
        );
      }),
      map(({ status }) => {
        const updatedState = structuredClone(this.stream$.getValue());
        snackbarService.pub.hide('savingBakery2bClient');
        if (status === C_Save_Operation_Status.SOS1_DATA_CHANGED) {
          snackbarService.pub.show({
            type: 'success',
            content: t('customer.customer_saved'),
          });
          updatedState.bakery2bAccounts = updatedState.bakery2bAccounts.map((el) => {
            if (el.id === updatedState.recordToSave?.id) {
              return updatedState.recordToSave;
            } else return el;
          });
        } else {
          snackbarService.pub.show({
            type: 'error',
            content: t('customer.customer_saved'),
          });
        }
        return updatedState;
      }),
    );
  }

  private addRecord(): Observable<IBakery2bTabState> {
    return this.actionListener(['addRecord']).pipe(
      tap(({ newRecord }) => {
        if (newRecord) {
          customerService.pub.connectBakery2bUserWithWawiCustomer({ data: newRecord });
          customerTabLoadingService.pub.loading(true);
        }
      }),
      switchMap((state) => {
        return customerService.sub.connectBakery2bUserWithWawiCustomer().pipe(
          responseHandler<ConnectBakery2bUserWithWawiCustomerRes | undefined>({
            success: () => 'customer.customer_saved',
            customErrorHandler: (err) => {
              customerTabLoadingService.pub.loading(false);
              return resolveBakery2bConnectError(err) ?? 'common.error_chnages_not_saved';
            },
          }),
          map((v) => {
            const newState = structuredClone(state);
            customerTabLoadingService.pub.loading(false);
            v && newState.bakery2bAccounts.push(v);
            newState.newRecord = null;
            return newState;
          }),
        );
      }),
    );
  }

  private deleteRecord(): Observable<IBakery2bTabState> {
    return this.actionListener(['deleteRecord']).pipe(
      tap(({ recordToRemove }) => {
        customerService.pub.disconnectBakery2bAccountFromCustomer({
          customerId: recordToRemove?.customerId ?? '',
          bakery2BClientId: recordToRemove?.bakery2BClientId ?? '',
        });
        customerTabLoadingService.pub.loading(true);
      }),
      switchMap((state) => {
        return customerService.sub.disconnectBakery2bAccountFromCustomer().pipe(
          responseHandler<DisconnectBakery2bAccountFromCustomerRes | undefined>({
            success: () => 'customer.customer_saved',
            customErrorHandler: () => {
              customerTabLoadingService.pub.loading(false);
              return 'common.error_chnages_not_saved';
            },
          }),
          map((v) => {
            const newState = structuredClone(state);
            customerTabLoadingService.pub.loading(false);

            if (v) {
              newState.bakery2bAccounts = newState.bakery2bAccounts.filter(
                (item) => item.id != state.recordToRemove?.bakery2BClientId,
              );
              newState.recordToRemove = null;
            }

            return newState;
          }),
        );
      }),
    );
  }

  private selectedCustomer(): Observable<IBakery2bTabState> {
    return customersListState.sub.state().pipe(
      filter(({ action }) => action === 'selectRecord'),
      filter(({ selectedRecord }) => typeof selectedRecord?.id === 'string'),
      switchMap(({ selectedRecord }) => {
        customerTabLoadingService.pub.loading(true);
        const id = selectedRecord!.id;
        const state = this.stream$.getValue();
        state.action = 'list.selectedRecord';
        return this.details(state, id);
      }),
    );
  }

  private details(state: IBakery2bTabState, id: string): Observable<IBakery2bTabState> {
    const details = customerService.sub.getCustomerBakery2bAccounts().pipe(
      responseHandler<Bakery2bAccountsRes>({
        errorReturnType: defaultCustomerBakery2bData,
      }),
      map((data) => {
        state.bakery2bAccounts = data;
        state.customerId = id;
        customerTabLoadingService.pub.loading(false);
        return state;
      }),
    );
    customerService.pub.getCustomerBakery2bAccounts({ id });
    return details;
  }
}

class Bakery2bTabState extends State<IBakery2bTabState> {
  pub = new PubImpl(this.stream$);
  sub = new SubImpl(this.stream$);
}

export const bakery2bTabState = new Bakery2bTabState({
  ...tabStateModel,
  action: undefined,
  bakery2bAccounts: [],
  recordToRemove: null,
  newRecord: null,
  recordToSave: null,
});

export interface IBakery2bTabState extends Pick<ITabState, 'customerId'> {
  action:
    | undefined
    | 'initBakery2bAccountsData'
    | 'list.selectedRecord'
    | 'addRecord'
    | 'deleteRecord'
    | 'internalUpdate'
    | 'saveRecord';
  bakery2bAccounts: ITabState['bakery2bAccounts'];
  recordToRemove: DisconnectWa_Bakery2bAccountFromCustomerMutationVariables | null;
  newRecord: Wa_ConnectBakery2bUserInput | null;
  recordToSave: ITabState['bakery2bAccounts'][number] | null;
}
